import { useRef, useState } from "react";
import {
  Container,
  Typography,
  Box,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { RestartAlt, ZoomIn, ZoomOut } from "@mui/icons-material";
import NextBreadcrumbs from "../component/NextBreadcrumbs";
import Page from "../component/Page";

const LATEST_MAPS_URL =
  "https://dlq8zkk4s2twe.cloudfront.net/productionNew/latest.json";
const LATEST_MAPS_BASE_URL = "https://dlq8zkk4s2twe.cloudfront.net";

const TRANSPORTATION_MAPS = [
  {
    key: "prcp",
    heading: "Current Rainfall",
    mapId: "5",
    variable: "PRCP",
    titleIncludes: ["precipitation", "since midnight"],
    alt: "Kentucky Mesonet precipitation since midnight map",
  },
  {
    key: "wspd",
    heading: "Current Windspeed",
    mapId: "8",
    variable: "WSPD",
    titleIncludes: ["wind speed", "direction"],
    alt: "Kentucky Mesonet wind speed and direction map",
  },
];

const pathHasVariable = (path, variable) =>
  path ? path.split("/").includes(variable) : false;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const clampView = (view, element) => {
  if (!element || view.scale <= 1) {
    return { scale: 1, x: 0, y: 0 };
  }

  const { width, height } = element.getBoundingClientRect();
  const contentWidth = width * view.scale;
  const contentHeight = height * view.scale;
  const minX = width - contentWidth;
  const minY = height - contentHeight;

  return {
    scale: view.scale,
    x: clamp(view.x, minX, 0),
    y: clamp(view.y, minY, 0),
  };
};

const findLatestMap = (files, config) => {
  const exactMatch = files.find((file) => String(file.mapId) === config.mapId);
  const titleMatch = files.find((file) => {
    const title = file.title?.toLowerCase() || "";
    return (
      pathHasVariable(file.path, config.variable) &&
      config.titleIncludes.every((text) => title.includes(text))
    );
  });
  const fallbackMatch = files.find((file) =>
    pathHasVariable(file.path, config.variable)
  );
  const file = exactMatch || titleMatch || fallbackMatch;

  if (!file?.path) {
    return null;
  }

  return {
    imageUrl: `${LATEST_MAPS_BASE_URL}/${file.path}-all.svg`,
  };
};

const SvgMapViewer = ({ src, alt }) => {
  const viewerRef = useRef(null);
  const dragRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });

  const updateZoom = (nextScale, point) => {
    setView((current) => {
      const scale = clamp(nextScale, 1, 5);

      if (scale === 1) {
        return { scale, x: 0, y: 0 };
      }

      const element = viewerRef.current;

      if (!element) {
        return current;
      }

      const ratio = scale / current.scale;
      const nextView = {
        scale,
        x: point.x - (point.x - current.x) * ratio,
        y: point.y - (point.y - current.y) * ratio,
      };

      return clampView(nextView, element);
    });
  };

  const zoomFromCenter = (factor) => {
    const element = viewerRef.current;

    if (!element) {
      return;
    }

    const { width, height } = element.getBoundingClientRect();
    updateZoom(view.scale * factor, { x: width / 2, y: height / 2 });
  };

  const handleWheel = (event) => {
    event.preventDefault();

    const rect = event.currentTarget.getBoundingClientRect();
    const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;

    updateZoom(view.scale * factor, {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const handlePointerDown = (event) => {
    if (view.scale <= 1) {
      return;
    }

    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
    };
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current) {
      return;
    }

    setView((current) =>
      clampView(
        {
          scale: current.scale,
          x: dragRef.current.originX + event.clientX - dragRef.current.startX,
          y: dragRef.current.originY + event.clientY - dragRef.current.startY,
        },
        viewerRef.current
      )
    );
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const resetView = () => setView({ scale: 1, x: 0, y: 0 });

  return (
    <Box
      ref={viewerRef}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        position: "relative",
        height: { xs: 300, md: 600 },
        overflow: "hidden",
        backgroundColor: "#efefef",
        border: "1px solid",
        borderColor: "divider",
        touchAction: "none",
        cursor: view.scale > 1 ? "grab" : "default",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        draggable={false}
        loading="lazy"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          userSelect: "none",
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          transformOrigin: "0 0",
        }}
      />
      <Stack
        direction="row"
        spacing={0.5}
        onPointerDown={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          backgroundColor: "rgba(255,255,255,0.92)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tooltip title="Zoom in">
          <IconButton
            aria-label="Zoom in"
            size="small"
            onClick={() => zoomFromCenter(1.2)}
          >
            <ZoomIn fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <IconButton
            aria-label="Zoom out"
            size="small"
            onClick={() => zoomFromCenter(1 / 1.2)}
          >
            <ZoomOut fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset map">
          <IconButton aria-label="Reset map" size="small" onClick={resetView}>
            <RestartAlt fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const LatestMapImage = ({ config, map }) => (
  <Box sx={{ py: 1 }}>
    <Typography variant="h4">{config.heading}</Typography>
    {map?.imageUrl ? (
      <Box sx={{ mt: 2 }}>
        <SvgMapViewer src={map.imageUrl} alt={config.alt} />
      </Box>
    ) : (
      <Typography sx={{ py: 3 }}>Latest map unavailable.</Typography>
    )}
  </Box>
);

export default function Transportation({ maps = {} }) {
  return (
    <Page>
      <img
        src="./background/transportation.jpg"
        alt=""
        style={{
          width: "100%",
          maxHeight: 250,
          objectFit: "cover",
          objectPosition: "center bottom",
        }}
      ></img>
      <Container sx={{ py: 5 }}>
        <NextBreadcrumbs></NextBreadcrumbs>
        <Box sx={{ py: 1 }}>
          <Typography variant="h3">Transportation</Typography>
          <Typography sx={{ textAlign: "justify", py: 3 }}>
            Kentucky has nearly 80,000 miles of roadway, over 14,000 bridges,
            and over 2,500 miles of railway, critical infrastructure for
            maintaining the state's economic vitality and well-being. Yet
            weather and climate are common impediments impacting transportation;
            severe winds, heavy rainfall, snow and ice, and fog can cause major
            travel problems. Long-term climatic changes, such as increased
            rainfall and higher temperatures, can shorten pavement life and
            undermine bridge supports. More appropriate roadway design, pavement
            materials, and salting operations, carefully determined utilizing
            local climate information, can save millions of dollars annually in
            operations and maintenance costs. More accurate, more local weather
            and climate data are key to safer roads and highways across the
            Commonwealth.
          </Typography>
        </Box>
        {TRANSPORTATION_MAPS.map((config) => (
          <LatestMapImage
            key={config.key}
            config={config}
            map={maps[config.key]}
          />
        ))}
      </Container>
    </Page>
  );
}

export async function getServerSideProps() {
  try {
    const response = await fetch(LATEST_MAPS_URL);

    if (!response.ok) {
      throw new Error(`Failed to load latest maps: ${response.status}`);
    }

    const latestMaps = await response.json();
    const files = Array.isArray(latestMaps.files) ? latestMaps.files : [];
    const maps = TRANSPORTATION_MAPS.reduce((result, config) => {
      result[config.key] = findLatestMap(files, config);
      return result;
    }, {});

    return { props: { maps } };
  } catch (error) {
    console.error(error);
    return { props: { maps: {} } };
  }
}